import React, { useState } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

function TacrolimusCalculator({user}) {
    const [weight, setWeight] = useState ('');
    const [height, setHeight] = useState ('');
    const [age, setAge] =  useState('');
    const [gender, setGender] = useState('male');
    const [route, setRoute] = useState('');
    const [mainIndication, setMainIndication] = useState("General Therapeutic Range");
    const [subTimeline, setSubTimeline] = useState("");
    const [currentDose, setCurrentDose] = useState('');
    const [interval, setInterval] = useState('');
    const [measuredLevel, setMeasuredLevel] = useState('');
    const [simDose, setSimDose] = useState('');
    const [simInterval, setSimInterval] = useState('');




    // Step 1: Smart Weight Engine Logic
    const weightNum = parseFloat(weight) || 0;
    const heightNum = parseFloat(height) || 0;
    const ageNum = parseFloat(age) || 0;

    let ibw = 0;
    if (heightNum > 0 && gender) {
        const inchesOver5Foot = Math.max(0, (heightNum / 2.54) - 60);
        ibw = gender === 'male' ? 50 + (2.3 * inchesOver5Foot) : 45.5 + (2.3 * inchesOver5Foot);
    }

    // Select the correct operational weight
    const weightToUse = (ibw > 0 && weightNum > ibw) ? ibw : weightNum;
    const weightEngineStatus = ibw > 0 && weightNum > ibw 
        ? "Using IBW" 
        : heightNum > 0 ? "Using Actual" : "⚠️ Actual (No Ht Provided)";

    // Baseline Population Clearance Constants (from your handbook)
    // Adult: 0.06 L/h/kg | Pediatric (<18y): 0.14 L/h/kg
    const baseClFactor = ageNum < 18 ? 0.14 : 0.06;
    const clPopulation = baseClFactor * weightToUse;

    // Bioavailability Mapping from tdm1.png
    // Oral (Immediate/Extended): 0.25 | IV: 1.0
    const routeValue = {
        'iv': 1.0,
        'oral_ir': 0.25,
        'oral_er': 0.25
    }
    const F = routeValue[route]|| 0.25;
    const S = 1.0; // Salt factor is always 1.0 for Tacrolimus

    const TACROLIMUS_MATRIX = {
        "General Therapeutic Range": { default: "5 - 20" },
        "Liver Transplant (Months 1 - 12)": { default: "5 - 20" },
        "Kidney Transplant": {
            "Months 0-6 (Low Rejection Risk)": "6 - 12",
            "Months 0-6 (Moderate Rejection Risk)": "8 - 12",
            "Months 0-6 (High Rejection Risk)": "8 - 15",
            "Months 7-12 (Low Rejection Risk)": "5 - 8",
            "Months 7-12 (Moderate Rejection Risk)": "5 - 10",
            "Months 7-12 (High Rejection Risk)": "6 - 12",
            "Months >12 (Low Rejection Risk)": "4 - 8",
            "Months >12 (Moderate Rejection Risk)": "5 - 10",
            "Months >12 (High Rejection Risk)": "6 - 12"
        },
        "Heart Transplant": {
            "Months 1 - 2": "10 - 15",
            "Months 3 - 6": "8 - 12",
            "Months >6": "5 - 10"
        },
        "Crohn's Disease": { default: "10 - 20" },
        "Lung Transplant": { default: "5 - 15" },
        "Myasthenia Gravis": { default: "8 - 9" },
        "Prevention of Graft vs Host Disease": { default: "10 - 20" }
    };
    // Helper logic to find the exact target range string
    const getTacrolimusTarget = () => {
        const selectedData = TACROLIMUS_MATRIX[mainIndication];
        if (!selectedData) return "5 - 20";
        
        // If it has specific sub-timelines/risks, return that selection
        if (selectedData[subTimeline]) {
            return selectedData[subTimeline];
        }
        
        // Otherwise, drop back to the standard static default value
        return selectedData.default || Object.values(selectedData)[0] || "5 - 20";
    };

    const activeTargetRange = getTacrolimusTarget();
    const hasSubOptions = mainIndication === "Kidney Transplant" || mainIndication === "Heart Transplant";

    // Step 3: Dosage Prediction Variables & Math
    const currentDoseNum = parseFloat(currentDose) || 0;
    const intervalNum = parseFloat(interval) || 12; // Tacrolimus is usually given BD (Q12H)

    // Core Steady-State Trough Formula: Css = (Dose * S * F * 1000) / (CL * Interval)
    // We multiply by 1000 to cleanly convert mg/L into mcg/L (ng/mL)
    const predictedTacroCss = (clPopulation > 0 && currentDoseNum > 0)
        ? (currentDoseNum * S * F * 1000) / (clPopulation * intervalNum)
        : 0;

    // Evaluate predicted baseline level against the Step 2 matrix selection
    const checkTacroRegimenSuitability = () => {
        if (activeTargetRange.includes(" - ")) {
            const [min, max] = activeTargetRange.split(" - ").map(Number);
            if (predictedTacroCss < min) return { label: "EXPECTED SUBTHERAPEUTIC", color: "#3498db" };
            if (predictedTacroCss > max) return { label: "EXPECTED SUPRATHERAPEUTIC", color: "#e74c3c" };
            return { label: "EXPECTED WITHIN TARGET", color: "#2ecc71" };
        }
        return { label: "CHECK RANGE", color: "#95a5a6" };
    };

    const suitability = predictedTacroCss > 0 
        ? checkTacroRegimenSuitability() 
        : { label: "AWAITING REGIMEN INPUT", color: "#7f8c8d" };
    
    // Step 4: Clinical Correlation & Back-Calculations
    const actualLevelNum = parseFloat(measuredLevel) || 0;

    // 1. Calculate how far the patient's real blood deviates from the population model (%)
    const tacroDeviation = (predictedTacroCss > 0 && actualLevelNum > 0)
        ? ((actualLevelNum - predictedTacroCss) / predictedTacroCss) * 100
        : 0;

    // 2. Back-calculate the patient's TRUE individualized clearance (L/hr)
    // Formula derived from rearraging: Css = (Dose * S * F * 1000) / (CL * Interval)
    // CL_indiv = (Dose * S * F * 1000) / (ActualCss * Interval)
    const individualTacroCL = (actualLevelNum > 0 && currentDoseNum > 0)
        ? (currentDoseNum * S * F * 1000) / (actualLevelNum * intervalNum)
        : 0;

    // 3. Classify the patient's metabolic performance
    const getTacroMetabolismStatus = () => {
        if (tacroDeviation > 25) {
            return { 
                text: "Slow Metabolizer (High Risk of Accumulation & Nephrotoxicity)", 
                color: "#e67e22", 
                note: "The patient clears Tacrolimus significantly slower than average. Small doses will cause high levels. Check for CYP3A4 inhibitors (e.g., Diltiazem, Erythromycin, Azole antifungals)." 
            };
        }
        if (tacroDeviation < -25) {
            return { 
                text: "Rapid Metabolizer (High Risk of Subtherapeutic Levels & Rejection)", 
                color: "#3498db", 
                note: "The patient clears Tacrolimus very quickly (likely a CYP3A5 expresser). They will require much higher doses than normal to maintain immunosuppression. Check for enzyme inducers or non-compliance." 
            };
        }
        return { 
            text: "Standard Metabolizer", 
            color: "#2ecc71", 
            note: "The patient's metabolic rate aligns perfectly with your handbook's baseline population values." 
        };
    };

    const tacroMetabolism = actualLevelNum > 0 
        ? getTacroMetabolismStatus() 
        : { text: "Awaiting Lab Trough Level", color: "#7f8c8d", note: "Input the patient's measured plasma concentration to view metabolic variation." };

    // === RE-ENGINEERED DOSE SIMULATION CORE (INDIVIDUAL HIGH-PRECISION) ===
    const simDoseNum = parseFloat(simDose) || 0;
    const simIntervalNum = parseFloat(simInterval) || 12;

    let simulatedTacroCss = 0;

    if (simDoseNum > 0 && currentDoseNum > 0 && actualLevelNum > 0) {
        // Individual linear ratio scaling that accounts for both dose shifts AND dosing interval changes!
        const currentRate = currentDoseNum / intervalNum;
        const proposedRate = simDoseNum / simIntervalNum;
        
        if (currentRate > 0) {
            simulatedTacroCss = (proposedRate / currentRate) * actualLevelNum;
        }
    } else if (simDoseNum > 0 && clPopulation > 0 && simIntervalNum > 0) {
        // Fallback to population constants ONLY if no measured lab baseline exists yet
        simulatedTacroCss = (simDoseNum * S * F * 1000) / (clPopulation * simIntervalNum);
    }

    // Check if the simulated adjustment hits the target range chosen in Step 2
    const checkSimulatedTacroStatus = (val) => {
        if (activeTargetRange.includes(" - ")) {
            const [min, max] = activeTargetRange.split(" - ").map(Number);
            if (val < min) return { label: "EXPECTED SUBTHERAPEUTIC", color: "#3498db" };
            if (val > max) return { label: "EXPECTED TOXIC RANGE", color: "#e74c3c" };
            return { label: "EXPECTED TARGET ACHIEVED", color: "#2ecc71" };
        }
        return { label: "CHECK TARGET", color: "#95a5a6" };
    };

    const simSuitability = simulatedTacroCss > 0 
        ? checkSimulatedTacroStatus(simulatedTacroCss) 
        : { label: "AWAITING SIMULATION INPUT", color: "#7f8c8d" };
    
    // =========================================================
    // STEP 6: RANGE EVALUATION LOGIC
    // =========================================================

    // 1. Check suitability based entirely on the REAL lab result
    const checkActualRangeStatus = () => {
        if (activeTargetRange.includes(" - ")) {
            const [min, max] = activeTargetRange.split(" - ").map(Number);
            if (actualLevelNum < min) return "SUBTHERAPEUTIC";
            if (actualLevelNum > max) return "SUPRATHERAPEUTIC";
            return "WITHIN TARGET";
        }
        return "CHECK RANGE";
    };

    // 2. Track the active clinical status of the patient's blood level
    const actualStatus = actualLevelNum > 0 ? checkActualRangeStatus() : "PENDING";

    // 3. Dynamic header color selector based on reality status
    const getHeaderColor = () => {
        if (actualStatus === "WITHIN TARGET") return "#2ecc71";  // Green
        if (actualStatus === "SUBTHERAPEUTIC") return "#3498db"; // Blue
        if (actualStatus === "SUPRATHERAPEUTIC") return "#e74c3c"; // Red
        return "#7f8c8d";                                        // Grey fallback
    };

    let tacroInterpretation = "";
    let tacroRecommendation = "";

    if (actualLevelNum > 0) {
        if (actualStatus === "WITHIN TARGET") {
            tacroInterpretation = `The measured trough level of ${actualLevelNum.toFixed(1)} mcg/L is currently optimal and sits within the therapeutic target window (${activeTargetRange} mcg/L).`;
            tacroRecommendation = "Maintain current dose. Continue regular monitoring of whole blood trough levels, serum creatinine, potassium levels, and blood pressure. ";
        } else if (actualStatus === "SUBTHERAPEUTIC") {
            tacroInterpretation = `The measured trough level of ${actualLevelNum.toFixed(1)} mcg/L is subtherapeutic (Target: ${activeTargetRange} mcg/L), exposing the patient to an elevated risk of acute graft rejection.`;
            tacroRecommendation = simDoseNum > 0 
                ? `CRITICAL STEP: Thoroughly counsel the patient on medication adherence. If non-compliance is suspected, do NOT scale up dosage aggressively. If compliance is confirmed, titrate the dose upward. Your simulation shows that a new dose of ${simDoseNum}mg every ${simIntervalNum}h is expected to raise the level to ${simulatedTacroCss.toFixed(1)} mg/L. If response is good, continue current dose.`
                : "Thoroughly investigate patient compliance and check for hidden drug-drug interactions CYP3A4 inducers (e.g., Antimicrobials: Caspofungin, Nafcillin, Rifampicin ; Anticonvulsants: Carbamazepine, Phenobarbital, Phenytoin, Primidone ; Others: St. John’s wort). If a dose change is required, titrate up cautiously in small increments.";
        } else if (actualStatus === "SUPRATHERAPEUTIC") {
            tacroInterpretation = `The measured level of ${actualLevelNum.toFixed(1)} mcg/L is supratherapeutic. Elevated risk of acute calcineurin-inhibitor nephrotoxicity, neurotoxicity (tremors, headaches), and hyperglycemia.`;
            tacroRecommendation = actualLevelNum > 20
                ? "⚠️ HIGH TOXICITY WARNING: Hold the next dose immediately. Reduce the ongoing maintenance dose and monitor serum creatinine and electrolytes closely. Screen for recent additions of CYP3A4 inhibitors (e.g.,Calcium channel blockers: Diltiazem, Verapamil, Amlodipine ; Antibiotics: Clarithromycin, Erythromycin ; Antifungal agents: Fluconazole, Itraconazole, Ketoconazole ; Protease inhibitors: Indinavir, Ritonavir ; Prokinetic agents: Metoclopramide ; Others: Bromocriptine, Danazol, Ethinyl estradiol, Grapefruit juice, Methylprednisolone)."
                : "Consider withholding or decreasing the maintenance dose immediately. Monitor renal profiles closely and re-check trough values in 48 hours.";
        }
    }

    return (
        <section style={{ padding: '25px', background: '#1b1f23', border: '2px solid #3498db', borderRadius: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                <button 
                    type="button"
                    className="no-print" 
                    onClick={() => {
                                    const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
                                    const trackingLabel = `${actualUserEmail}-printed-Tacrolimus`;
                                    logUserActivityQuietly(trackingLabel); 
                                    window.print();
                                }} 
                    style={{
                    padding: '10px 20px',
                    backgroundColor: '#27ae60', 
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                    }}
                >
                    🖨️ Print PDF Report
                </button>
            </div>
            <h2 style = {{textAlign: 'center', color: '#3498db'}}>Tacrolimus TDM Worksheet</h2>
            <details style={{
                backgroundColor: '#1E293B',
                border: '1px solid #38BDF8',
                borderRadius: '8px',
                padding: '10px 15px',
                marginBottom: '20px',
                cursor: 'pointer'
            }}>
                <summary style={{ color: '#38BDF8', fontWeight: 'bold' }}>
                ⏱️ Check Clinical Sampling Protocol (Click to expand)
                </summary>
                <div style={{ marginTop: '12px', padding: '15px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px', border: '1px solid #3d4e5d', fontSize: '0.9rem', color: '#F8FAFC' }}>
    
                    {/* SECTION 1: TIMELINE & CRITICAL SAMPLING METHODOLOGY */}
                    <div style={{ marginBottom: '15px' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#00d2ff', textTransform: 'uppercase', display: 'block', marginBottom: '6px', letterSpacing: '0.5px' }}>
                            ⏱️ Kinetic Timeline & Sampling Restrictions
                        </span>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                            <tbody>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff', width: '35%' }}>Steady State Window:</td>
                                    <td style={{ padding: '8px 0', color: '#bdc3c7' }}>
                                        Draw steady-state whole blood levels **3 – 5 days** after initiating therapy or implementing a dosage adjustment.
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#e67e22' }}>Sampling Rule:</td>
                                    <td style={{ padding: '8px 0', color: '#fff', fontWeight: '500' }}>
                                        ⚠️ **Trough Sample Only.** Standard monitoring must strictly utilize true trough values drawn within **30 minutes or immediately before** the next scheduled dose. Peak values are invalid for regular monitoring.
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#9b59b6' }}>Tube Requirement:</td>
                                    <td style={{ padding: '8px 0', color: '#fff', fontWeight: 'bold' }}>
                                        💜 **Use EDTA Tube** (Mandatory pre-analytical rule. Tacrolimus partitions heavily into erythrocytes; a whole blood matrix is required for assay extraction).
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* SECTION 2: GLOBAL TARGET RANGE WINDOW */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '15px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        {/* Baseline Range Card */}
                        <div style={{ background: 'rgba(46, 204, 113, 0.06)', border: '1px solid rgba(46, 204, 113, 0.2)', padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                            <div style={{ fontWeight: 'bold', color: '#2ecc71', fontSize: '0.8rem', textTransform: 'uppercase' }}>🎯 General Blueprint Reference Range:</div>
                            <div style={{ fontSize: '1.4rem', fontWeight: 'bold', marginTop: '4px', color: '#fff' }}>
                                5 - 20 <span style={{ fontSize: '0.85rem', fontWeight: 'normal', color: '#bdc3c7' }}>mcg/L</span>
                            </div>
                            <span style={{ fontSize: '0.7rem', color: '#95a5a6', display: 'block', marginTop: '4px' }}>
                                *Note: For specific post-transplant timelines (Heart/Kidney matrices), refer to the Step 2 selector engine below.
                            </span>
                        </div>
                    </div>

                    {/* SECTION 3: CRITICAL CALCINEURIN INHIBITOR TOXICITY ALERTS */}
                    <div style={{ background: 'rgba(231, 76, 60, 0.08)', borderLeft: '4px solid #e74c3c', padding: '10px', borderRadius: '0 6px 6px 0' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#e74c3c', textTransform: 'uppercase', display: 'block' }}>
                            ⚠️ High Toxicity Exposure Risks
                        </span>
                        <div style={{ fontSize: '0.85rem', color: '#ecf0f1', marginTop: '4px', lineHeight: '1.4' }}>
                            • Concentrations exceeding upper target thresholds elevate risks of acute **calcineurin-inhibitor (CNI) nephrotoxicity**, severe hyperkalemia, and neurotoxicity (fine resting hand tremors, severe headaches).<br />
                            • If severe over-exposure occurs, consider withholding or minimizing doses while tracking daily serum creatinine clearances.
                        </div>
                    </div>

                </div>
            </details>
            <h3 style={{ color: '#3498db', marginTop: 0, fontSize: '1.2rem' }}>
                Step 1: Population-Based Clearance & Weight Optimization
            </h3>

            {/* Primary Demographics Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Age (Years):</label>
                    <input type="number" value={age} onChange={(e)=>setAge(e.target.value)} placeholder="e.g. 45" style={inputStyle} />
                </div>
                <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Gender:</label>
                    <select value={gender} onChange={(e)=>setGender(e.target.value)} style={inputStyle}>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                    </select>
                </div>
                <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Actual Weight (kg):</label>
                    <input type="number" value={weight} onChange={(e)=>setWeight(e.target.value)} placeholder="e.g. 70" style={inputStyle} />
                </div>
                <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Height (cm) <span style={{color:'#bdc3c7', fontWeight:'normal' }}>(Optional)</span>:</label>
                    <input type="number" value={height} onChange={(e)=>setHeight(e.target.value)} placeholder="Omit if unknown" style={inputStyle} />
                </div>
            </div>

            {/* Secondary Regimen Parameters Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Formulation Route:</label>
                    <select value={route} onChange={(e)=>setRoute(e.target.value)} style={inputStyle}>
                        <option value="oral_ir">Oral Immediate Release (Prograf) [F=0.25]</option>
                        <option value="oral_er">Oral Extended Release (Advagraf) [F=0.25]</option>
                        <option value="iv">IV Injection [F=1.0]</option>
                    </select>
                </div>

                {/* Smart Weight Output Window */}
                <div style={{ textAlign: 'center', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '6px' }}>
                    <span style={{ fontSize: '0.65rem', color: '#bdc3c7', display: 'block', fontWeight: 'bold' }}>WEIGHT CALCULATOR VALUE</span>
                    <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#f1c40f' }}>{weightToUse.toFixed(1)} kg</span>
                    <span style={{ fontSize: '0.65rem', color: '#fff', display: 'block', fontStyle: 'italic', marginTop: '2px' }}>
                        {weightEngineStatus}
                    </span>
                </div>
            </div>

            {/* Visual Calculation Transparency Box */}
            <div style={{ background: '#fdf6e3', padding: '12px', borderRadius: '6px', fontSize: '0.85rem', color: '#2c3e50' }}>
                <strong>Math Proof:</strong> {baseClFactor} L/h/kg ({ageNum < 18 ? "Pediatric baseline" : "Adult baseline"}) × {weightToUse.toFixed(1)} kg = 
                <strong style={{ color: '#d35400', fontSize: '1.1rem', marginLeft: '5px' }}>{clPopulation.toFixed(2)} L/hr</strong>
            </div>
            <h3 style={{ color: '#16a085', marginTop: '20px', fontSize: '1.2rem' }}>
                Step 2: Clinical Indication & Timeline Stratification
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: hasSubOptions ? '1fr 1fr' : '1fr', gap: '15px', marginBottom: '15px' }}>
                {/* Primary Indication Selector */}
                <div>
                    <label style={{ fontWeight: 'bold', fontSize: '0.8rem', display: 'block', marginBottom: '5px', color: '#2c3e50' }}>
                        Primary Indication:
                    </label>
                    <select 
                        value={mainIndication} 
                        onChange={(e) => {
                            setMainIndication(e.target.value);
                            setSubTimeline(""); // Clear sub-timeline on primary change
                        }}
                        style={{ width: '100%', padding: '10px', background: '#ecf0f1', border: '1px solid #bdc3c7', color: '#2c3e50', borderRadius: '5px', fontWeight: '500' }}
                    >
                        {Object.keys(TACROLIMUS_MATRIX).map(ind => (
                            <option key={ind} value={ind}>{ind}</option>
                        ))}
                    </select>
                </div>

                {/* Dynamic Sub-Timeline Selector (Only visible for Kidney / Heart) */}
                {hasSubOptions && (
                    <div>
                        <label style={{ fontWeight: 'bold', fontSize: '0.8rem', display: 'block', marginBottom: '5px', color: '#2c3e50' }}>
                            Timeline & Risk Stratification:
                        </label>
                        <select 
                            value={subTimeline} 
                            onChange={(e) => setSubTimeline(e.target.value)}
                            style={{ width: '100%', padding: '10px', background: '#ecf0f1', border: '1px solid #bdc3c7', color: '#2c3e50', borderRadius: '5px', fontWeight: '500' }}
                        >
                            <option value="">-- Select Timeline / Risk Profile --</option>
                            {Object.keys(TACROLIMUS_MATRIX[mainIndication]).map(subKey => (
                                <option key={subKey} value={subKey}>{subKey}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Live Target Display Window */}
            <div style={{ background: '#e8f8f5', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '1px solid #1abc9c' }}>
                <span style={{ fontSize: '0.8rem', color: '#16a085', fontWeight: 'bold', textTransform: 'uppercase' }}>
                    Target Whole Blood Trough Concentration (C0):
                </span>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#1abc9c', marginTop: '4px' }}>
                    {activeTargetRange} <small style={{ fontSize: '0.9rem' }}>mcg/L</small>
                </div>
            </div>
            <h3 style={{ color: '#e67e22', marginTop: 0, fontSize: '1.2rem' }}>
                Step 3: Dosage Prediction & Initial Suitability Target
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                {/* Total Dose Input */}
                <div>
                    <label style={{ fontWeight: 'bold', fontSize: '0.8rem', display: 'block', marginBottom: '5px' }}>
                        Current Dose per Administration (mg):
                    </label>
                    <input 
                        type="number" 
                        step="0.5"
                        value={currentDose} 
                        onChange={(e) => setCurrentDose(e.target.value)} 
                        placeholder="e.g. 2.0"
                        style={inputStyle} 
                    />
                    <span style={{ fontSize: '0.7rem', color: '#bdc3c7', display: 'block', marginTop: '4px' }}>
                        *Note: Enter individual dose (e.g. 2mg for a 2mg BD regimen).
                    </span>
                </div>

                {/* Dosing Interval Input */}
                <div>
                    <label style={{ fontWeight: 'bold', fontSize: '0.8rem', display: 'block', marginBottom: '5px' }}>
                        Dosing Interval (Hours):
                    </label>
                    <input 
                        type="number" 
                        value={interval} 
                        onChange={(e) => setInterval(e.target.value)} 
                        placeholder="e.g. 12"
                        style={inputStyle} 
                    />
                </div>
            </div>

            {/* Predicted Trough Concentration Window */}
            <div style={{ background: 'rgba(0,0,0,0.2)', padding: '18px', borderRadius: '8px', textAlign: 'center', border: `1px solid ${suitability.color}` }}>
                <span style={{ fontSize: '0.75rem', color: '#bdc3c7', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                    ESTIMATED BASELINE TROUGH (Css):
                </span>
                <div style={{ fontSize: '2.4rem', fontWeight: 'bold', color: suitability.color, margin: '6px 0' }}>
                    {predictedTacroCss > 0 ? predictedTacroCss.toFixed(1) : '0.0'} <span style={{ fontSize: '1.1rem' }}>mcg/L</span>
                </div>
                
                {/* Evaluation Status Badge */}
                <div style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: 'bold', 
                    color: '#fff', 
                    background: suitability.color, 
                    display: 'inline-block', 
                    padding: '4px 14px', 
                    borderRadius: '12px',
                    textTransform: 'uppercase'
                }}>
                    {suitability.label}
                </div>
            </div>

            {/* Live Mathematical Formula Trace Box */}
            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '6px', fontSize: '0.75rem', color: '#bdc3c7', marginTop: '12px' }}>
                <strong>Regimen Proof:</strong> ({currentDoseNum} mg × {F.toFixed(2)} F) / ({clPopulation.toFixed(2)} L/hr × {intervalNum} hr) × 1000 = {predictedTacroCss.toFixed(1)} mcg/L
            </div>
            <h3 style={{ color: '#9b59b6', marginTop: 0, fontSize: '1.2rem' }}>
                Step 4: Clinical Correlation & Metabolic Fingerprinting
            </h3>

            <div style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem', display: 'block', marginBottom: '6px', color: '#fff' }}>
                    Actual Measured Whole Blood Trough Level (C0) (mcg/L):
                </label>
                <input 
                    type="number" 
                    step="0.1"
                    value={measuredLevel} 
                    onChange={(e) => setMeasuredLevel(e.target.value)} 
                    placeholder="Enter precise lab result (ng/mL or mcg/L)"
                    style={inputStyle} 
                />
            </div>

            {actualLevelNum > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
                    {/* Deviation Indicator Box */}
                    <div style={{ background: 'rgba(0,0,0,0.25)', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div style={{ fontSize: '0.7rem', color: '#bdc3c7', fontWeight: 'bold', textTransform: 'uppercase' }}>Kinetic Variance</div>
                        <div style={{ fontSize: '1.6rem', fontWeight: 'bold', color: tacroDeviation > 0 ? '#e74c3c' : '#3498db', margin: '4px 0' }}>
                            {tacroDeviation > 0 ? '+' : ''}{tacroDeviation.toFixed(1)}%
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#bdc3c7' }}>vs. Population Baseline</div>
                    </div>

                    {/* True Personalized Clearance Box */}
                    
                    <div style={{ background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>INDIVIDUAL CL VS <small style = {{frontSize: '0.9rem',color: '#33d930'}}>POPULATION CL</small>  </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fff' }}>
                            {individualTacroCL.toFixed(2)} <small style={{ fontSize: '0.7rem' }}>L/hr</small> VS <small style = {{color: '#33d930'}}>{clPopulation.toFixed(2)} L/hr</small>
                        </div>
                    </div>

                    {/* Metabolism Profile Explainer Banner */}
                    <div style={{ gridColumn: 'span 2', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', borderLeft: `5px solid ${tacroMetabolism.color}` }}>
                        <div style={{ fontWeight: 'bold', color: tacroMetabolism.color, fontSize: '0.9rem' }}>
                            {tacroMetabolism.text}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#bdc3c7', marginTop: '4px', lineHeight: '1.4' }}>
                            {tacroMetabolism.note}
                        </div>
                    </div>
                </div>
            )}
            <h3 style={{ color: '#2ecc71', marginTop: 0, fontSize: '1.2rem' }}>
                Step 5: Dose Titration Simulator (Sandbox Mode)
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', alignItems: 'center' }}>
                {/* Simulation Inputs Panel */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                        <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#bdc3c7', display: 'block', marginBottom: '4px' }}>
                            Proposed New Dose per Administration (mg):
                        </label>
                        <input 
                            type="number" 
                            step="0.5"
                            value={simDose}
                            onChange={(e) => setSimDose(e.target.value)}
                            placeholder="e.g. 2.5"
                            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                            disabled={!measuredLevel || !currentDose}
                        />
                    </div>
                    <div>
                        <label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#bdc3c7', display: 'block', marginBottom: '4px' }}>
                            Proposed Interval (hours):
                        </label>
                        <input 
                            type="number" 
                            value={simInterval}
                            onChange={(e) => setSimInterval(e.target.value)}
                            placeholder="e.g. 12"
                            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                            disabled={!measuredLevel || !currentDose}
                        />
                    </div>
                    <p style={{ fontSize: '0.7rem', color: '#bdc3c7', margin: '4px 0 0 0', fontStyle: 'italic' }}>
                        *Titrate cautiously, usually in 0.5mg - 1mg steps, to minimize sudden changes in renal function.
                    </p>
                </div>

                {/* Live Simulation Projection Window */}
                <div style={{ background: 'rgba(0,0,0,0.3)', padding: '20px', borderRadius: '10px', textAlign: 'center', border: `1px solid ${simSuitability.color || '#34495e'}` }}>
                    <span style={{ fontSize: '0.7rem', color: '#bdc3c7', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                        SIMULATED TROUGH LEVEL (C0):
                    </span>
                    <div style={{ fontSize: '2.4rem', fontWeight: 'bold', color: '#fff', margin: '6px 0' }}>
                        {simulatedTacroCss > 0 ? simulatedTacroCss.toFixed(1) : '0.0'} <span style={{ fontSize: '1rem', fontWeight: 'normal', color: '#bdc3c7' }}>mcg/L</span>
                    </div>
                    
                    {/* Target Match Status Badge */}
                    {simulatedTacroCss > 0 && (
                        <div style={{ 
                            fontSize: '0.7rem', 
                            fontWeight: 'bold', 
                            color: '#fff', 
                            background: simSuitability.color, 
                            display: 'inline-block', 
                            padding: '4px 12px', 
                            borderRadius: '10px',
                            textTransform: 'uppercase',
                            marginBottom: '8px'
                        }}>
                            {simSuitability.label}
                        </div>
                    )}
                    
                    <div style={{ fontSize: '0.65rem', color: '#95a5a6', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '6px' }}>
                        Engine Profile: {actualLevelNum > 0 ? "🎯 Personalized Patient Kinetic Model" : "⚠️ Standard Population Model"}
                    </div>
                </div>
            </div>

            {/* VISUAL SPECTRUM PROGRESS SLIDER TRACK BAR */}
            {simulatedTacroCss > 0 && (() => {
                const rangeStr = activeTargetRange; // e.g. "5 - 20" or "8 - 12"
                const [minTarget, maxTarget] = rangeStr.split(" - ").map(Number);
                
                // Standard max scale boundary cap set to 25 mcg/L context landscape for Tacrolimus
                const maxChartScale = 25;
                const leftPercent = (minTarget / maxChartScale) * 100;
                const widthPercent = ((maxTarget - minTarget) / maxChartScale) * 100;
                const pinPercent = (simulatedTacroCss / maxChartScale) * 100;

                return (
                    <div style={{ padding: '15px', background: '#111416', borderRadius: '8px', border: '1px solid #2c3539', marginTop: '20px' }}>
                        <div style={{ position: 'relative', height: '14px', background: '#2c3e50', borderRadius: '7px', margin: '15px 0 15px 0', overflow: 'visible' }}>
                            
                            {/* TARGET THERAPEUTIC SHADOW HIGHLIGHT */}
                            <div style={{ 
                                position: 'absolute', 
                                left: `${Math.max(0, Math.min(90, leftPercent))}%`, 
                                width: `${Math.max(10, Math.min(100, widthPercent))}%`, 
                                height: '100%', 
                                background: 'rgba(46, 204, 113, 0.22)', 
                                borderLeft: '1px dashed #2ecc71', 
                                borderRight: '1px dashed #2ecc71',
                                borderRadius: '4px'
                            }} />

                            {/* ACTIVE PINPOINT INDICATOR NODE */}
                            <div style={{
                                position: 'absolute',
                                left: `${Math.max(0, Math.min(98, pinPercent))}%`,
                                transform: 'translateX(-50%)',
                                width: '18px',
                                height: '18px',
                                top: '-2px',
                                background: '#f1c40f',
                                borderRadius: '50%',
                                boxShadow: '0 0 12px #f1c40f',
                                border: '2px solid #fff',
                                transition: 'left 0.2s ease-out',
                                zIndex: 10
                            }} />
                        </div>

                        {/* GRAPH SCALING LABELS */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: '#7f8c8d', padding: '0 2px', marginBottom: '12px' }}>
                            <span>0 mcg/L</span>
                            <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>Target Spec: {rangeStr} mcg/L</span>
                            <span>25 mcg/L</span>
                        </div>

                        {/* CLEAR LINEAR CALCULATED RATIO TRACE AUDIT FOOTER */}
                        <div style={{ fontSize: '0.68rem', color: '#57606f', borderTop: '1px solid rgba(255,255,255,0.03)', paddingTop: '8px', lineHeight: '1.4', textAlign: 'left' }}>
                            {actualLevelNum > 0 ? (
                                <>
                                    Formula Predict Css: [(Proposed Dose / Proposed Interval) / (Current Dose / Current Interval)] × Measured Level<br />
                                    Individual Patient Linear Rate Ratio Trace: [({simDoseNum} mg / {simIntervalNum}h) / ({currentDoseNum} mg / {intervalNum}h)] × {measuredLevel} mcg/L = {simulatedTacroCss.toFixed(1)} mcg/L
                                </>
                            ) : (
                                <>
                                    Formula Predict Css: (Proposed Dose × S × F × 1000) / (Population CL × Proposed Interval)<br />
                                    Population Estimate Reference Trace: ({simDoseNum}mg × 1.0 × {F} × 1000) / ({clPopulation.toFixed(2)} L/hr × {simIntervalNum}hr)
                                </>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* STEP 6 UI CONTAINER BOX */}
            <div style={{ 
                marginTop: '25px', 
                padding: '20px', 
                background: '#1b1f23', 
                borderRadius: '12px', 
                borderLeft: `8px solid ${getHeaderColor()}`, 
                boxShadow: '0 4px 15px rgba(0,0,0,0.4)'
            }}>
                <h4 style={{ color: getHeaderColor(), margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Step 6: Clinical Interpretation & Action Plan
                </h4>

                {/* Assessment Block */}
                <div style={{ marginBottom: '15px' }}>
                    <div style={{ color: '#95a5a6', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Pharmacokinetic Assessment:
                    </div>
                    <div style={{ color: '#ecf0f1', fontSize: '0.95rem', marginTop: '4px', lineHeight: '1.5' }}>
                        {actualLevelNum > 0 ? (
                            <span>
                                <span style={{ color: getHeaderColor(), fontWeight: 'bold', background: 'rgba(255,255,255,0.05)', padding: '2px 8px', borderRadius: '4px', marginRight: '8px', fontSize: '0.85rem' }}>
                                    {actualStatus}
                                </span>
                                <span>{tacroInterpretation}</span>
                            </span>
                        ) : (
                            <span style={{ color: '#7f8c8d', fontStyle: 'italic' }}>Awaiting Step 4 input to formulate clinical assessment.</span>
                        )}
                    </div>
                </div>

                {/* Proposed Plan Block */}
                <div style={{ padding: '15px', background: 'rgba(255,255,255,0.04)', borderRadius: '8px' }}>
                    <div style={{ color: '#2ecc71', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        Proposed Pharmacist Care Plan:
                    </div>
                    <div style={{ color: '#fff', fontSize: '0.95rem', marginTop: '6px', lineHeight: '1.5' }}>
                        {actualLevelNum > 0 ? tacroRecommendation : "Care plan recommendation will generate automatically once a lab level is recorded."}
                    </div>
                </div>
            </div>

            {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
            <p style={{ margin: '12px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
                *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
            </p>
        </section>
        
    )
}
const inputStyle = {
    width: '80%',
    padding: '8px',
    marginTop: '5px',
    borderRadius: '4px',
    border: '1px solid #ccc'
};
export default TacrolimusCalculator;